/**
 * Minimalerei Buy-Box JS — Phase 3.D.2
 *
 * <minimalerei-material-picker> custom element.
 *
 * Cards are <button>s (not radio inputs). Card-click finds Dawn's
 * hidden radio inside <variant-selects> matching the option-value
 * and triggers a 'change' event on it. Dawn's variant-selects
 * listener catches the change → publishes optionValueSelectionChange
 * → product-info.js fetches updated section HTML → re-renders all
 * blocks (incl. our material-picker with the new variant's selected
 * state).
 *
 * No direct event listener on Dawn's pub-sub bus needed — section
 * reload is the source of truth. If reload doesn't happen (e.g.,
 * variant has no separate URL/SKU), the click handler also updates
 * card active-state immediately for instant visual feedback.
 */

/**
 * Render a price string into a target element. Detects whether the
 * target was server-rendered in compact format (= has a child .price-
 * item__whole span) and preserves that structure on update. Plain
 * format stays plain. Keeps the same Liquid parsing logic that lives
 * in main-product.liquid: strip €, split at comma, render whole +
 * superscript decimals + trailing currency.
 */
function renderPriceInto(target, money) {
  const isCompact = target.querySelector('.price-item__whole') !== null;
  if (!isCompact) {
    target.textContent = money;
    return;
  }

  const cleaned = money.replace(/€/g, '').trim();
  const commaIdx = cleaned.indexOf(',');
  if (commaIdx === -1) {
    target.textContent = money;
    return;
  }

  const whole = cleaned.slice(0, commaIdx).trim();
  const decimals = cleaned.slice(commaIdx + 1).trim().split(' ')[0];
  if (!whole || !decimals) {
    target.textContent = money;
    return;
  }

  target.innerHTML =
    '<span class="price-item__whole">' + whole + '</span>' +
    '<sup class="price-item__decimals">,' + decimals + '</sup>' +
    '<span class="price-item__currency">€</span>';
}

class MinimalereiMaterialPicker extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-material-card]'));
    if (this.cards.length === 0) return;

    // Option name is "Farbe" by default but can be "Material" or any other
    // — Liquid passes via data-option-name. Used for radio targeting +
    // aria labels.
    this.optionName = this.dataset.optionName || 'Farbe';
    this.currentEl = this.querySelector('[data-current-variant]');

    this.bindEvents();
  }

  bindEvents() {
    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.handleCardClick(card));
    });
  }

  handleCardClick(card) {
    const value = card.dataset.materialValue;
    if (!value) return;

    // Find Dawn's hidden radio inside <variant-selects>. Radio names follow
    // "{option.name}-{option.position}" pattern (e.g. "Farbe-1", "Material-1").
    const namePrefix = this.optionName + '-';
    const radio = document.querySelector(
      `input[type="radio"][name^="${CSS.escape(namePrefix)}"][value="${CSS.escape(value)}"]`
    );

    if (radio) {
      radio.checked = true;
      // Dispatch 'change' so Dawn's <variant-selects> picks it up,
      // publishes optionValueSelectionChange, and product-info.js
      // reloads the section.
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Card active states + current-display text.
    this.cards.forEach((c) => {
      const isActive = c === card;
      c.classList.toggle('is-active', isActive);
      c.setAttribute('aria-pressed', String(isActive));
    });

    this.updateCurrentDisplay(card);

    // Direct DOM updates for the case when <variant-selects> is absent or
    // Dawn's section-reload chain doesn't fire (e.g. user has only the
    // Material-Picker block, no variant_picker block in the customizer).
    // The data-variant-* attributes are emitted by Liquid using the first
    // variant matching this option-value.
    this.applyVariantUpdates(card);
  }

  applyVariantUpdates(card) {
    const variantId = card.dataset.variantId;
    const variantPrice = card.dataset.variantPrice;
    const variantAvailable = card.dataset.variantAvailable === 'true';
    if (!variantId) return;

    const priceAmount = document.querySelector('.buy-box__price-amount');
    if (priceAmount && variantPrice) renderPriceInto(priceAmount, variantPrice);

    const atcPrice = document.querySelector('.buy-box__atc-price');
    if (atcPrice && variantPrice) atcPrice.textContent = variantPrice;

    const idInput = document.querySelector('input[name="id"].product-variant-id');
    if (idInput) idInput.value = variantId;

    const atc = document.querySelector('.buy-box__atc');
    if (atc) atc.disabled = !variantAvailable;
  }

  /**
   * Display = sublabel if set in material_labels metafield, else raw
   * variant value. Shop-owner has full control via metafield content;
   * we don't dedupe/concatenate (the prior substring-match approach
   * was fragile with diacritics, e.g. "Rosé" vs "rose...").
   */
  updateCurrentDisplay(card) {
    if (!this.currentEl) return;
    const value = card.dataset.materialValue || '';
    const sublabel = card.dataset.materialSublabel || '';
    this.currentEl.textContent = sublabel || value;
  }
}

if (!customElements.get('minimalerei-material-picker')) {
  customElements.define('minimalerei-material-picker', MinimalereiMaterialPicker);
}

/* <minimalerei-upload-zone> Custom Element was removed in favor of a
   pure CSS adjacent-sibling approach (see buy-box.css "Drop-zone
   styling" section). Upload-Lift's app-block stays in its original
   DOM position; we just style it as a sibling of .buy-box__upload-
   zone. Avoids breaking filepond's internal init logic that depends
   on its app-block parent staying put. */

/**
 * Phase 3.D.3 — Mirror price-block into ATC button on variant change.
 *
 * Dawn's product-info.js does targeted innerHTML-replace on tracked IDs
 * after fetching the new section HTML — that covers our minimalerei_price
 * block (id="price-{section.id}") but NOT the ATC button's .buy-box__
 * atc-price span inside the action_row block.
 *
 * Approach: MutationObserver on the price-amount span. Whenever Dawn
 * updates the price block, copy the text to the ATC button's price
 * label. Observer is robust to PUB-SUB timing/availability and survives
 * section reloads (the parent IDs are stable across re-renders).
 */
function initBuyBoxAtcPriceMirror() {
  const productInfoEl = document.querySelector('product-info');
  const sectionId = productInfoEl && productInfoEl.dataset && productInfoEl.dataset.section;
  if (!sectionId) return;

  const priceBlock = document.getElementById('price-' + sectionId);
  if (!priceBlock) return;

  const sync = () => {
    const amount = priceBlock.querySelector('.buy-box__price-amount');
    const atcPrice = document.querySelector('.buy-box__atc-price');
    if (amount && atcPrice) {
      atcPrice.textContent = amount.textContent.trim();
    }
  };

  const observer = new MutationObserver(sync);
  observer.observe(priceBlock, { childList: true, subtree: true, characterData: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBuyBoxAtcPriceMirror);
} else {
  initBuyBoxAtcPriceMirror();
}
